import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { TrafficTicket, TicketStatus } from '../types';
import { ArrowLeft, MapPin, Calendar, DollarSign, User, Car, FileText, Download, AlertCircle, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { ReceiptService } from '../services/ReceiptService';
import { PaymentReminderService, PaymentReminder } from '../services/PaymentReminderService';

const TicketDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<TrafficTicket | null>(null);
  const [loading, setLoading] = useState(true);
  const [officerInfo, setOfficerInfo] = useState<{ name: string; badgeNumber: string } | null>(null);
  const [reminders, setReminders] = useState<PaymentReminder[]>([]);
  const [selectedImage, setSelectedImage] = useState<{ url: string; title: string } | null>(null);

  // Calculate days remaining for a ticket (default 18 days from creation)
  const getDaysRemaining = (ticket: TrafficTicket): number => {
    const dueDate = new Date(ticket.createdAt);
    dueDate.setDate(dueDate.getDate() + 18);
    const now = new Date();
    const diffTime = dueDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Format days remaining for display
  const formatDaysRemaining = (days: number): string => {
    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} left`;
    } else if (days === 0) {
      return 'Due today';
    } else {
      return `${Math.abs(days)} day${Math.abs(days) > 1 ? 's' : ''} overdue`;
    }
  };

  // Get color class based on days remaining
  const getDaysRemainingColor = (days: number): string => {
    if (days > 7) return 'text-green-600';
    if (days > 3) return 'text-yellow-600';
    if (days >= 0) return 'text-orange-600';
    return 'text-red-600';
  };

  useEffect(() => {
    fetchTicketDetails();
  }, [id]);

  const fetchTicketDetails = async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const ticketDoc = await getDoc(doc(db, 'trafficTickets', id));
      
      if (ticketDoc.exists()) {
        const data = ticketDoc.data();
        const ticketData: TrafficTicket = {
          id: ticketDoc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          paymentDate: data.paymentDate?.toDate(),
          violatorDOB: data.violatorDOB?.toDate(),
          offenceTime: data.offenceTime?.toDate(),
        } as TrafficTicket;
        
        setTicket(ticketData);
        
        // Fetch payment reminders for this ticket
        const ticketReminders = await PaymentReminderService.getRemindersForTicket(id);
        setReminders(ticketReminders);
        
        // Fetch officer information if not already present
        if (!ticketData.officerName && ticketData.officerId) {
          try {
            const officerDoc = await getDoc(doc(db, 'users', ticketData.officerId));
            if (officerDoc.exists()) {
              const officerData = officerDoc.data();
              setOfficerInfo({
                name: officerData.fullName || 'N/A',
                badgeNumber: officerData.badgeNumber || 'N/A'
              });
            }
          } catch (error) {
            console.error('Error fetching officer info:', error);
          }
        } else if (ticketData.officerName) {
          // Use existing officer info from ticket
          setOfficerInfo({
            name: ticketData.officerName,
            badgeNumber: ticketData.officerBadgeNumber || 'N/A'
          });
        }
      }
    } catch (error) {
      console.error('Error fetching ticket:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="text-center py-12">
        <FileText className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Ticket not found</h3>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 text-blue-600 hover:text-blue-700"
        >
          Go back
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Ticket Details</h1>
          <p className="text-gray-600 mt-1">
            {ticket.ticketCode || ticket.id.slice(0, 8)}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Violation Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Offence Type</label>
                <p className="text-gray-900 font-medium mt-1">{ticket.offenceType}</p>
              </div>
              {ticket.customOffence && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Custom Offence</label>
                  <p className="text-gray-900 font-medium mt-1">{ticket.customOffence}</p>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-gray-500">Fine Amount</label>
                <p className="text-gray-900 font-medium mt-1">
                  EC${ticket.fineAmount?.toLocaleString() || 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Status</label>
                <span
                  className={`inline-flex mt-1 px-3 py-1 text-sm font-semibold rounded-full ${
                    ticket.status === TicketStatus.PAID
                      ? 'bg-green-100 text-green-800'
                      : ticket.status === TicketStatus.WARRANT_ISSUED
                      ? 'bg-red-100 text-red-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}
                >
                  {ticket.status}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Reminders Section */}
          {ticket.status === TicketStatus.ISSUED && reminders.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <AlertCircle size={20} className="text-orange-600" />
                Payment Reminders
              </h2>
              <div className="space-y-3">
                {reminders.map((reminder) => (
                  <div
                    key={reminder.id}
                    className={`p-4 rounded-lg border ${
                      reminder.reminderType === 'critical'
                        ? 'bg-red-50 border-red-200'
                        : reminder.reminderType === 'overdue'
                        ? 'bg-orange-50 border-orange-200'
                        : 'bg-yellow-50 border-yellow-200'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {reminder.reminderType === 'due_soon' ? (
                        <Clock className="w-5 h-5 text-yellow-600 mt-0.5" />
                      ) : reminder.reminderType === 'overdue' ? (
                        <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-sm">
                            {reminder.reminderType === 'due_soon' && 'Payment Due Soon'}
                            {reminder.reminderType === 'overdue' && 'Payment Overdue'}
                            {reminder.reminderType === 'critical' && 'Urgent: Payment Required'}
                          </h4>
                          {reminder.reminderType === 'critical' && (
                            <span className="text-xs font-bold text-red-600 bg-red-100 px-2 py-1 rounded">
                              URGENT
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {reminder.daysOverdue > 0
                            ? `${reminder.daysOverdue} day${reminder.daysOverdue > 1 ? 's' : ''} overdue`
                            : 'Due soon'}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-sm font-medium">
                            EC${reminder.fineAmount.toLocaleString()}
                          </span>
                          <span className="text-xs text-gray-500">
                            Due: {format(reminder.dueDate, 'MMM dd, yyyy')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <button
                onClick={() => navigate(`/payment/${ticket.id}`)}
                className="w-full mt-4 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Pay Now
              </button>
            </div>
          )}

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <User size={20} />
              Violator Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Name</label>
                <p className="text-gray-900 font-medium mt-1">{ticket.violatorName}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Address</label>
                <p className="text-gray-900 mt-1">{ticket.address}</p>
              </div>
              {ticket.licenceNumber && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Licence Number</label>
                  <p className="text-gray-900 font-medium mt-1">{ticket.licenceNumber}</p>
                </div>
              )}
              {ticket.violatorDOB && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Date of Birth</label>
                  <p className="text-gray-900 mt-1">
                    {format(ticket.violatorDOB, 'MMM dd, yyyy')}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Car size={20} />
              Vehicle Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {ticket.licensePlate && (
                <div>
                  <label className="text-sm font-medium text-gray-500">License Plate</label>
                  <p className="text-gray-900 font-medium mt-1">{ticket.licensePlate}</p>
                </div>
              )}
              {ticket.vehicleMake && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Make</label>
                  <p className="text-gray-900 mt-1">{ticket.vehicleMake}</p>
                </div>
              )}
              {ticket.vehicleModel && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Model</label>
                  <p className="text-gray-900 mt-1">{ticket.vehicleModel}</p>
                </div>
              )}
              {ticket.vehicleYear && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Year</label>
                  <p className="text-gray-900 mt-1">{ticket.vehicleYear}</p>
                </div>
              )}
              {ticket.vehicleColor && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Color</label>
                  <p className="text-gray-900 mt-1">{ticket.vehicleColor}</p>
                </div>
              )}
            </div>
          </div>

          {(ticket.vehiclePhotoUrl || ticket.driverLicencePhotoUrl) && (
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Photos</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {ticket.vehiclePhotoUrl && (
                  <div>
                    <label className="text-sm font-medium text-gray-500 mb-2 block">
                      Vehicle Photo
                    </label>
                    <img
                      src={ticket.vehiclePhotoUrl}
                      alt="Vehicle"
                      className="w-full h-48 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => setSelectedImage({ url: ticket.vehiclePhotoUrl!, title: 'Vehicle Photo' })}
                    />
                  </div>
                )}
                {ticket.driverLicencePhotoUrl && (
                  <div>
                    <label className="text-sm font-medium text-gray-500 mb-2 block">
                      Driver's Licence
                    </label>
                    <img
                      src={ticket.driverLicencePhotoUrl}
                      alt="Driver's Licence"
                      className="w-full h-48 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => setSelectedImage({ url: ticket.driverLicencePhotoUrl!, title: "Driver's Licence" })}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Ticket Information</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Calendar className="text-gray-400 mt-1" size={20} />
                <div>
                  <label className="text-sm font-medium text-gray-500">Date Issued</label>
                  <p className="text-gray-900 mt-1">
                    {format(ticket.createdAt, 'MMM dd, yyyy')}
                  </p>
                  <p className="text-sm text-gray-500">
                    {format(ticket.createdAt, 'h:mm a')}
                  </p>
                </div>
              </div>

              {ticket.status === TicketStatus.ISSUED && (
                <div className="flex items-start gap-3">
                  <Clock className={`mt-1 ${getDaysRemainingColor(getDaysRemaining(ticket))}`} size={20} />
                  <div>
                    <label className="text-sm font-medium text-gray-500">Days Remaining</label>
                    <p className={`font-semibold mt-1 ${getDaysRemainingColor(getDaysRemaining(ticket))}`}>
                      {formatDaysRemaining(getDaysRemaining(ticket))}
                    </p>
                  </div>
                </div>
              )}

              {ticket.offenceTime && (
                <div className="flex items-start gap-3">
                  <Calendar className="text-gray-400 mt-1" size={20} />
                  <div>
                    <label className="text-sm font-medium text-gray-500">Offence Time</label>
                    <p className="text-gray-900 mt-1">
                      {format(ticket.offenceTime, 'MMM dd, yyyy h:mm a')}
                    </p>
                  </div>
                </div>
              )}

              {ticket.paymentDate && (
                <div className="flex items-start gap-3">
                  <DollarSign className="text-green-600 mt-1" size={20} />
                  <div>
                    <label className="text-sm font-medium text-gray-500">Payment Date</label>
                    <p className="text-gray-900 mt-1">
                      {format(ticket.paymentDate, 'MMM dd, yyyy')}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-3">
                <MapPin className="text-gray-400 mt-1" size={20} />
                <div>
                  <label className="text-sm font-medium text-gray-500">Location</label>
                  {ticket.parish && <p className="text-gray-900 mt-1">{ticket.parish}</p>}
                  {ticket.island && <p className="text-sm text-gray-500">{ticket.island}</p>}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Officer Information</h2>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-500">Officer Name</label>
                <p className="text-gray-900 font-medium mt-1">
                  {officerInfo?.name || ticket.officerName || 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Badge Number</label>
                <p className="text-gray-900 font-medium mt-1">
                  {officerInfo?.badgeNumber || ticket.officerBadgeNumber || 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {ticket.status === TicketStatus.ISSUED && (
            <button
              onClick={() => navigate(`/payment/${ticket.id}`)}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Pay Now
            </button>
          )}
          
          {ticket.status === TicketStatus.PAID && (
            <button
              onClick={() => ReceiptService.downloadReceipt(ticket)}
              className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center justify-center gap-2"
            >
              <Download size={20} />
              Download Receipt
            </button>
          )}
        </div>
      </div>

      {/* Full Screen Image Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative w-full h-full flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 bg-black bg-opacity-50">
              <h3 className="text-white text-lg font-semibold">{selectedImage.title}</h3>
              <button
                onClick={() => setSelectedImage(null)}
                className="text-white hover:text-gray-300 transition-colors"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Image */}
            <div className="flex-1 flex items-center justify-center p-4">
              <img
                src={selectedImage.url}
                alt={selectedImage.title}
                className="max-w-full max-h-full object-contain"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketDetailPage;
